import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/** Совпадает с _sidebar.scss: max-width (map-get($grid-breakpoints, 'lg') - 0.02px) */
const MOBILE_SIDEBAR_MEDIA = "(max-width: 991.98px)";

const SidebarNavContext = createContext(null);

export const SidebarNavProvider = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(MOBILE_SIDEBAR_MEDIA).matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_SIDEBAR_MEDIA);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const toggleMobileSidebar = useCallback(() => {
    setMobileOpen((v) => !v);
  }, []);

  const closeMobileSidebar = useCallback(() => setMobileOpen(false), []);

  const value = useMemo(
    () => ({
      mobileOpen,
      isMobile,
      toggleMobileSidebar,
      closeMobileSidebar,
    }),
    [mobileOpen, isMobile, toggleMobileSidebar, closeMobileSidebar]
  );

  return (
    <SidebarNavContext.Provider value={value}>
      {children}
    </SidebarNavContext.Provider>
  );
};

export const useSidebarNavOptional = () => useContext(SidebarNavContext);

export const useSidebarNav = () => {
  const ctx = useContext(SidebarNavContext);
  if (!ctx) {
    throw new Error("useSidebarNav must be used within SidebarNavProvider");
  }
  return ctx;
};

/** Затемнение контента: клик и Escape закрывают мобильное меню. */
export const MobileSidebarBackdrop = () => {
  const { mobileOpen, isMobile, closeMobileSidebar } = useSidebarNav();

  useEffect(() => {
    if (!mobileOpen || !isMobile) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeMobileSidebar();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen, isMobile, closeMobileSidebar]);

  if (!isMobile || !mobileOpen) return null;

  return (
    <button
      type="button"
      className="sidebar-mobile-backdrop"
      onClick={closeMobileSidebar}
      aria-label="Закрыть меню"
    />
  );
};
